var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Collapsible, Tabs } from '@showroomjs/ui';
import * as React from 'react';
import { useCodeBlocks } from '../lib/codeblocks-context';
import { useHighlights } from '../lib/use-highlights';
import { PreviewConsoleProvider } from '../lib/use-preview-console';
import { PropsEditorProvider } from '../lib/use-props-editor';
import { useTargetAudience } from '../lib/use-target-audience';
import { A11yResultPanel } from './a11y-result-panel';
import { A11ySummary } from './a11y-summary';
import { Div } from './base';
import { CodePreviewIframe, } from './code-preview-iframe';
import { CodePreviewShowroomFrame } from './code-preview-showroom-frame';
import { ConsolePanel } from './console-panel';
import { LinkToStandaloneView } from './link-to-standalone-view';
import { MeasuringButton } from './measuring-button';
import { PropsEditorPanel } from './props-editor-panel';
export const CodePlayground = (_a) => {
    var { hasPropsEditor, frame, initialHeight, height, initialWidth, id } = _a, props = __rest(_a, ["hasPropsEditor", "frame", "initialHeight", "height", "initialWidth", "id"]);
    const [showDetails, setShowDetails] = React.useState(!hasPropsEditor);
    const targetAudience = useTargetAudience();
    const codeBlocks = useCodeBlocks();
    const matchedCodeData = codeBlocks[props.code];
    const initialHeightValue = initialHeight && Number(initialHeight);
    const heightValue = height && Number(height);
    const initialWidthValue = initialWidth && Number(initialWidth);
    const frameRef = React.useRef(null);
    const [isMeasuring, setIsMeasuring] = React.useState(false);
    const highlightCurrentFrameItems = useHighlights();
    const highlightFrameItems = React.useCallback((selectors, newColor) => {
        if (frame) {
            if (frameRef.current) {
                frameRef.current.sendToChild({
                    type: 'highlightElements',
                    selectors,
                    color: newColor,
                });
            }
        }
        else {
            highlightCurrentFrameItems(selectors, newColor);
        }
    }, [highlightCurrentFrameItems]);
    const [activeTab, setActiveTab] = React.useState('props');
    return (React.createElement(PropsEditorProvider, { codeHash: matchedCodeData && matchedCodeData.initialCodeHash },
        React.createElement(PreviewConsoleProvider, null,
            React.createElement(Div, { css: Object.assign({ position: 'relative', roundedT: '$base' }, (frame
                    ? {
                        backgroundColor: '$gray-400',
                        borderBottomRightRadius: '$base',
                        width: '100%',
                    }
                    : {
                        minHeight: 48,
                        border: '1px solid',
                        borderColor: '$gray-300',
                    })) }, frame ? (React.createElement(CodePreviewIframe, { code: props.code, lang: props.lang, codeHash: matchedCodeData && matchedCodeData.initialCodeHash, initialHeight: initialHeightValue && !isNaN(initialHeightValue)
                    ? initialHeightValue
                    : undefined, height: heightValue && !isNaN(heightValue) ? heightValue : undefined, initialWidth: initialWidthValue && !isNaN(initialWidthValue)
                    ? initialWidthValue
                    : undefined, imperativeRef: frameRef, resizable: true })) : (React.createElement(CodePreviewShowroomFrame, { code: props.code, lang: props.lang }))),
            React.createElement(ConsolePanel, null)),
        React.createElement(Collapsible.Root, { open: showDetails, onOpenChange: setShowDetails },
            React.createElement(Div, { css: {
                    display: 'flex',
                    justifyContent: 'space-between',
                    py: '$1',
                } },
                React.createElement(Collapsible.Button, { css: {
                        display: 'flex',
                        alignItems: 'center',
                        gap: '$1',
                        fontSize: '$sm',
                        lineHeight: '$sm',
                    } },
                    React.createElement(Collapsible.ToggleIcon, { hide: showDetails, "aria-label": showDetails ? 'Hide Details' : 'View Details', width: "16", height: "16" }),
                    "Details"),
                React.createElement(Div, { css: {
                        display: 'inline-flex',
                        gap: '$1',
                    } },
                    React.createElement(A11ySummary, { onClick: () => {
                            setShowDetails(true);
                            setActiveTab('a11y');
                        }, css: {
                            px: '$2',
                            borderRight: '1px solid $gray-300',
                        } }),
                    frame && (React.createElement(React.Fragment, null,
                        React.createElement(MeasuringButton, { onClick: () => {
                                if (frameRef.current) {
                                    const nextIsMeasuring = !isMeasuring;
                                    frameRef.current.sendToChild({
                                        type: 'toggleMeasure',
                                        active: nextIsMeasuring,
                                    });
                                    setIsMeasuring(nextIsMeasuring);
                                }
                            }, isActive: isMeasuring }))),
                    React.createElement(LinkToStandaloneView, { codeHash: matchedCodeData && matchedCodeData.initialCodeHash, isDesigner: targetAudience === 'designer' }))),
            React.createElement(Collapsible.Content, { animate: true }, hasPropsEditor ? (React.createElement(Div, { css: {
                    backgroundColor: '$gray-200',
                } },
                React.createElement(A11yResultPanel, { highlightItems: highlightFrameItems }))) : (React.createElement(Tabs.Root, { value: activeTab, onValueChange: (tab) => {
                    setActiveTab(tab);
                    if (tab === 'props') {
                        highlightFrameItems([], '');
                    }
                } },
                React.createElement(Tabs.List, null,
                    React.createElement(Tabs.Trigger, { value: "props" }, "Props"),
                    React.createElement(Tabs.Trigger, { value: "a11y" }, "Accessibility")),
                React.createElement(Tabs.Content, { value: "props" },
                    React.createElement(PropsEditorPanel, null)),
                React.createElement(Tabs.Content, { value: "a11y" },
                    React.createElement(A11yResultPanel, { highlightItems: highlightFrameItems }))))))));
};
//# sourceMappingURL=code-playground.js.map